import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { API_CONFIG } from '@/api/config';
class SignalRService {
    constructor() {
        Object.defineProperty(this, "connection", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "groupRefCounts", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        });
        Object.defineProperty(this, "getAccessToken", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "pendingGroupOps", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: Promise.resolve()
        });
    }
    init(getAccessToken) {
        this.getAccessToken = getAccessToken;
    }
    buildConnection() {
        const conn = new HubConnectionBuilder()
            .withUrl(`${API_CONFIG.BASE_URL}/api/realtime`, {
            accessTokenFactory: async () => (await this.getAccessToken?.()) ?? '',
        })
            .withAutomaticReconnect()
            .configureLogging(LogLevel.Warning)
            .build();
        // Re-subscribe all active groups on reconnect (connectionId changes)
        conn.onreconnected(async () => {
            const connectionId = conn.connectionId;
            if (!connectionId)
                return;
            for (const group of this.groupRefCounts.keys()) {
                await this.subscribeOnServer(connectionId, group);
            }
        });
        return conn;
    }
    async getAuthHeaders() {
        const token = await this.getAccessToken?.();
        const headers = { 'Content-Type': 'application/json' };
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        return headers;
    }
    async subscribeOnServer(connectionId, group) {
        const response = await fetch(`${API_CONFIG.BASE_URL}/api/realtime/groups/subscribe`, {
            method: 'POST',
            headers: await this.getAuthHeaders(),
            body: JSON.stringify({ connectionId, group }),
        });
        if (!response.ok) {
            throw new Error(`SignalR subscribe failed for group ${group}: ${response.status}`);
        }
    }
    async unsubscribeOnServer(connectionId, group) {
        const response = await fetch(`${API_CONFIG.BASE_URL}/api/realtime/groups/subscribe`, {
            method: 'DELETE',
            headers: await this.getAuthHeaders(),
            body: JSON.stringify({ connectionId, group }),
        });
        if (!response.ok) {
            throw new Error(`SignalR unsubscribe failed for group ${group}: ${response.status}`);
        }
    }
    enqueue(op) {
        const result = this.pendingGroupOps.then(() => op());
        this.pendingGroupOps = result.catch(console.error);
        return result;
    }
    async joinGroup(group) {
        return this.enqueue(async () => {
            const current = this.groupRefCounts.get(group) ?? 0;
            if (current > 0) {
                this.groupRefCounts.set(group, current + 1);
                return; // already joined
            }
            if (!this.connection) {
                this.connection = this.buildConnection();
                await this.connection.start();
            }
            const connectionId = this.connection.connectionId;
            if (!connectionId) {
                throw new Error(`SignalR connectionId unavailable after start — cannot join group "${group}"`);
            }
            try {
                await this.subscribeOnServer(connectionId, group);
                // Only increment refcount after successful subscribe
                this.groupRefCounts.set(group, 1);
            }
            catch (err) {
                console.error(`Failed to subscribe to group ${group}:`, err);
                throw err;
            }
        });
    }
    async leaveGroup(group) {
        return this.enqueue(async () => {
            const current = this.groupRefCounts.get(group) ?? 0;
            if (current <= 0)
                return;
            const next = current - 1;
            if (next === 0) {
                this.groupRefCounts.delete(group);
                const connectionId = this.connection?.connectionId;
                if (connectionId) {
                    await this.unsubscribeOnServer(connectionId, group);
                }
                if (this.groupRefCounts.size === 0 && this.connection) {
                    await this.connection.stop();
                    this.connection = null;
                }
            }
            else {
                this.groupRefCounts.set(group, next);
            }
        });
    }
    on(event, handler) {
        this.connection?.on(event, handler);
    }
    off(event, handler) {
        this.connection?.off(event, handler);
    }
}
export const signalRService = new SignalRService();
